import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { MarkdownPreview } from '../MarkdownPreview';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

/**
 * Conocimiento — lo que el bot sabe responder por fuera del agendamiento.
 *
 * Se guarda sola contra PUT /kb. Si el tenant todavía no tiene archivo, lo crea
 * con POST /kb/init antes de dejar escribir.
 */
export function KnowledgeSection({ mode, tenantId, onSaved, testId }: TenantSectionProps<void>) {
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<SectionStatus>('unknown');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = useCallback(async (): Promise<SectionStatus> => {
    try {
      const st = await api.tenants.getKbStatus(tenantId);
      return st.status === 'synced' ? 'complete' : 'partial';
    } catch {
      // GAP-F: el chequeo de KB falla en dev. No es motivo para bloquear nada.
      return 'unknown';
    }
  }, [tenantId]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const kb = await api.tenants.getKb(tenantId);
        if (mounted) setContent(kb.markdown);
      } catch {
        try {
          await api.tenants.initKb(tenantId);
          if (mounted) setContent('');
        } catch (err) {
          if (mounted) setError((err as Error).message);
        }
      }
      const next = await refreshStatus();
      if (!mounted) return;
      setStatus(next);
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [tenantId, refreshStatus]);

  async function save() {
    setError(null);
    setSaving(true);
    try {
      await api.tenants.updateKb(tenantId, content);
      setSaved(true);
      setStatus(await refreshStatus());
      onSaved?.({ markdown: content });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="Conocimiento"
      description="Lo que el bot responde por fuera del agendamiento: horarios, dirección, preguntas frecuentes."
      status={status}
      error={error}
      saving={saving}
      saved={saved}
      saveDisabled={loading}
      onSave={save}
    >
      {loading ? (
        <p className="text-body-sm text-neutral-500 dark:text-neutral-400">Cargando contenido…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-group">
            <label htmlFor="kb-content" className="input-label">
              Contenido
            </label>
            <textarea
              id="kb-content"
              className="input input-md w-full min-h-[320px] font-mono text-sm"
              value={content}
              spellCheck={false}
              onChange={(e) => setContent(e.target.value)}
              placeholder={'# Horarios de atención\nLunes a viernes de 7:00 a 17:00.'}
            />
            <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
              Texto simple (Markdown). El bot lo usa para responder preguntas frecuentes.
            </p>
          </div>
          <div className="border border-neutral-200 dark:border-neutral-700 rounded-md p-4 overflow-y-auto max-h-[380px]">
            <p className="text-caption uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
              Vista previa
            </p>
            <MarkdownPreview content={content} />
          </div>
        </div>
      )}

      {status === 'unknown' && !loading && (
        <div className="toast toast-warning">
          <div className="toast-description">
            No pudimos verificar la búsqueda semántica en este ambiente. El bot igual responde con
            el texto completo: no bloquea la activación.
          </div>
        </div>
      )}
    </SectionShell>
  );
}
